import { Component } from '@theme/component';

/**
 * A custom element that formats rte content for easier styling
 */
class RTEFormatter extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#formatContent();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#formatContent();
  }

  #formatContent() {
    this.#formatProductDescription();
    this.querySelectorAll('table').forEach(this.#formatTable);
  }

  /** Groups recognized source headings while retaining the original content nodes. */
  #formatProductDescription() {
    const scope = this.closest('.gfm-product-description');
    if (!scope || this.querySelector(':scope > .gfm-product-description__section')) return;
    const labels = {
      details: scope.dataset.gfmDescriptionDetails,
      fit: scope.dataset.gfmDescriptionFit,
      care: scope.dataset.gfmDescriptionCare,
      returns: scope.dataset.gfmDescriptionReturns,
    };
    const headings = {
      'product description': 'details',
      'product details': 'details',
      description: 'details',
      details: 'details',
      'size advice': 'fit',
      'size guide': 'fit',
      'sizing guide': 'fit',
      'size chart': 'fit',
      sizing: 'fit',
      'size & fit': 'fit',
      'fit & sizing': 'fit',
      'fabric & care': 'care',
      'fabric and care': 'care',
      'care instructions': 'care',
      'material & care': 'care',
      'materials & care': 'care',
      returns: 'returns',
      'returns & refunds': 'returns',
      'returns & exchanges': 'returns',
    };
    const headingKind = (node) => {
      if (node.nodeType !== 1 || !['H2', 'H3', 'H4', 'H5', 'P'].includes(node.tagName)) return null;
      if (node.querySelector('a, img, picture, video, iframe, form, input, button, table, details')) return null;
      const title = node.textContent.trim().replace(/\s+/g, ' ').replace(/[:：]\s*$/, '').toLowerCase();
      const kind = Object.hasOwn(headings, title) ? headings[title] : null;
      return kind && (labels[kind] || kind === 'returns') ? kind : null;
    };
    const nodes = Array.from(this.childNodes);
    const groups = [];
    let current;
    for (const node of nodes) {
      const kind = headingKind(node);
      if (kind) {
        current = { heading: node, kind, nodes: [] };
        groups.push(current);
      } else if (current) {
        current.nodes.push(node);
      }
    }
    // Empty or ambiguous sections keep their original structure as a safe fallback.
    if (
      !groups.length ||
      groups.some((group) => !group.nodes.some((node) =>
        node.textContent.trim() || (node.nodeType === 1 && (
          ['IMG', 'PICTURE', 'VIDEO', 'IFRAME', 'TABLE'].includes(node.tagName) ||
          node.querySelector('img, picture, video, iframe, table')
        ))
      ))
    ) {
      return;
    }

    const fragment = document.createDocumentFragment();
    const groupByHeading = new Map(groups.map((group) => [group.heading, group]));
    let content;
    let previousKind;
    for (const node of nodes) {
      const group = groupByHeading.get(node);
      if (group) {
        // Adjacent source headings can share one category (Description / Details).
        // Keep their original heading and content inside the existing disclosure.
        if (content && previousKind === group.kind) {
          content.append(node);
          continue;
        }
        previousKind = group.kind;
        const details = document.createElement('details');
        details.className = 'gfm-product-description__section';
        const summary = document.createElement('summary');
        summary.textContent = labels[group.kind] || group.heading.textContent.trim().replace(/[:：]\s*$/, '');
        const toggle = document.createElement('span');
        toggle.className = 'gfm-product-description__toggle';
        toggle.setAttribute('aria-hidden', 'true');
        toggle.textContent = '+';
        summary.append(toggle);
        content = document.createElement('div');
        content.className = 'gfm-product-description__content';
        node.classList.add('gfm-product-description__source-heading');
        node.hidden = true;
        content.append(node);
        details.append(summary, content);
        fragment.append(details);
      } else if (content) {
        content.append(node);
      } else {
        fragment.append(node);
      }
    }
    this.replaceChildren(fragment);
  }

  /**
   * Formats a table for easier styling
   * @param {HTMLTableElement} table
   */
  #formatTable(table) {
    if (!table.parentElement?.classList.contains('rte-table-wrapper')) {
      const wrapper = document.createElement('div');
      wrapper.classList.add('rte-table-wrapper');
      const parent = table.parentNode;
      if (parent) {
        parent.insertBefore(wrapper, table);
        wrapper.appendChild(table);
      }
    }

    if (!table.closest('.gfm-shipping-page')) return;
    const headers = Array.from(table.tHead?.rows[0]?.cells ?? []);
    const rows = Array.from(table.tBodies).flatMap((group) => Array.from(group.rows));
    if (
      !headers.length ||
      !rows.length ||
      [...headers, ...rows.flatMap((row) => Array.from(row.cells))].some(
        (cell) => cell.colSpan !== 1 || cell.rowSpan !== 1
      ) ||
      rows.some((row) => row.cells.length !== headers.length)
    ) {
      return;
    }

    // Explicit roles retain the table relationships when mobile CSS stacks the cells.
    table.classList.add('gfm-shipping-table');
    table.setAttribute('role', 'table');
    table.tHead.setAttribute('role', 'rowgroup');
    for (const row of Array.from(table.tHead.rows)) {
      row.setAttribute('role', 'row');
      for (const cell of Array.from(row.cells)) cell.setAttribute('role', 'columnheader');
    }
    for (const group of Array.from(table.tBodies)) group.setAttribute('role', 'rowgroup');
    for (const row of rows) {
      row.setAttribute('role', 'row');
      Array.from(row.cells).forEach((cell, index) => {
        cell.setAttribute('role', cell.tagName === 'TH' ? 'rowheader' : 'cell');
        if (cell.tagName === 'TD') cell.dataset.label = headers[index].textContent.trim();
      });
    }
  }
}

if (!customElements.get('rte-formatter')) {
  customElements.define('rte-formatter', RTEFormatter);
}
